import { QueryFunctionContext } from '@tanstack/react-query';
import { api } from '../services/api';
import { IData, IDataNoPagination } from '../types/shared';

// ============================================================================
// Tipos
// ============================================================================

export type FechamentoStatus = 'ok' | 'pendente' | 'encerramento_pendente' | 'sem_dados';

export type RegimeChave = 'lr' | 'lp' | 'sn' | 'mei' | 'outros';

export interface IFechamentoModuloInfo {
  data: string | null;
  data_esperada: string | null;
  dias_atraso: number | null;
  status: FechamentoStatus;
}

export interface IRegimeStats {
  chave: RegimeChave;
  label: string;
  total: number;
  // Consolidado — pendentes = pendente + encerramento_pendente (mantido pra retrocompat)
  ok: number;
  pendentes: number;
  pct_ok: number;
  // Campos abaixo foram adicionados em 2026-05 — opcionais pra que o frontend
  // continue funcionando se chegar resposta de uma versao anterior do backend
  // (deploy desincronizado). Consumidores devem usar `?? 0` no fallback.
  ativas?: number;
  inativas?: number;
  pendente?: number;
  encerramento_pendente?: number;
  sem_dados?: number;
}

export interface IFechamentoTotalizadores {
  // Visao geral
  total_empresas: number;
  total_ativas: number;
  total_inativas: number;
  tempo_medio_cliente_dias: number | null;

  // Contabil (consolidado)
  contabil_ok: number;
  contabil_pendente: number;
  contabil_encerramento_pendente: number;
  contabil_sem_dados: number;
  pct_contabil_ok: number;
  pct_contabil_pendente: number;
  media_dias_atraso_pendentes: number | null;

  // Breakdown ATIVAS
  ativas_ok: number;
  ativas_pendentes: number;
  ativas_sem_dados: number;
  pct_ativas_ok: number;
  media_dias_atraso_ativas: number | null;

  // Breakdown INATIVAS
  inativas_ok: number;
  inativas_pendentes: number;
  inativas_sem_dados: number;
  pct_inativas_ok: number;
  media_dias_atraso_inativas: number | null;

  // Por regime
  por_regime: IRegimeStats[];
}

export interface ICalendarioEmpresaAmostra {
  codi_emp: string | null;
  nome: string | null;
}

export interface ICalendarioCelula {
  ano: number;
  mes: number;
  qtd: number;
  amostra: ICalendarioEmpresaAmostra[];
}

export interface IFechamentoCalendario {
  anos: number[];
  celulas: ICalendarioCelula[];
  qtd_max: number;
  total_empresas: number;
}

export interface IEmpresaFechamentoListItem {
  id: number;
  external_id: string | null;
  codi_emp: string | null;
  nome: string | null;
  razao_social: string | null;
  cnpj: string | null;
  regime: string | null;
  grupo_de_empresas: string | null;
  dt_cli_desde: string | null;
  dt_cli_ate: string | null;
  dias_como_cliente: number | null;

  contabil: IFechamentoModuloInfo;
}

export interface IEmpresaFechamentoDetalhe {
  empresa: IEmpresaFechamentoListItem;
}

export interface IFiltrosOptions {
  ufs: string[];
  cidades: string[];
  grupos: string[];
  comercial: string[];
  contabil: string[];
  fiscal: string[];
  pessoal: string[];
}

// ============================================================================
// Hook
// ============================================================================

export const useFechamentos = () => {
  const getTotalizadores = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IFechamentoTotalizadores>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IDataNoPagination<IFechamentoTotalizadores>>(
      '/api/v1/fechamento/totalizadores',
      { params: filters },
    );
    return res.data;
  };

  const getCalendario = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IFechamentoCalendario>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IDataNoPagination<IFechamentoCalendario>>(
      '/api/v1/fechamento/calendario',
      { params: filters },
    );
    return res.data;
  };

  const getEmpresas = async (
    key: QueryFunctionContext,
  ): Promise<IData<IEmpresaFechamentoListItem[]>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IData<IEmpresaFechamentoListItem[]>>('/api/v1/fechamento/empresas', {
      params: filters,
    });
    return res.data;
  };

  const getEmpresaDetalhe = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IEmpresaFechamentoDetalhe>> => {
    const empresaId = key.queryKey?.[1] as number | null | undefined;
    // queryKey[2] (opcional) carrega params do critério; sem ele o backend
    // cai no default ("mensal" — alinhado com CRITERIO_DEFAULT do front).
    const params = (key.queryKey?.[2] ?? {}) as Record<string, string | number>;
    if (empresaId === null || empresaId === undefined) {
      throw new Error('empresaId é obrigatório em getEmpresaDetalhe');
    }
    const res = await api.get<IDataNoPagination<IEmpresaFechamentoDetalhe>>(
      `/api/v1/fechamento/empresa/${empresaId}`,
      { params },
    );
    return res.data;
  };

  // Assinatura compativel com QueryFunctionContext mesmo nao tendo filtros hoje —
  // padroniza com o resto do hook e preserva extensibilidade futura.
  const getFiltrosOptions = async (
    _key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IFiltrosOptions>> => {
    const res = await api.get<IDataNoPagination<IFiltrosOptions>>(
      '/api/v1/fechamento/filtros-options',
    );
    return res.data;
  };

  return {
    getTotalizadores,
    getCalendario,
    getEmpresas,
    getEmpresaDetalhe,
    getFiltrosOptions,
  };
};
